// A maintenance option priced at the desk (#515): the contract over its years and its Yearly
// Summaries, from the repaint the Take Off already priced. Null for a repaint option. Pure.
import {
  defaultStartYear,
  maintenanceSetup,
  maintenanceYearsOf,
  OPTION_KIND,
  priceContract,
  programmesOf,
  yearlySummary,
  type MaintenanceSetup,
  type OptionColumns,
  type PricedContract,
  type PricedOption,
  type PricingRates,
  type PricingReferenceFromApi,
  type SectionInput,
  type YearlySummary,
} from '@higgins/m2q-pricing';
import type { DeviceRecord } from './device-db';
import type { ReviewQuestionFromApi } from './types';

/** The REVIEW's maintenance questions (reference-data/review-questions.json): asked only on a version with a maintenance option. */
const MAINTENANCE_GROUP = 'Maintenance';

export interface MaintenancePricing {
  years: number;
  startYear: number;
  tier: string | null;
  /** The labour type servicing is on: the set-up's, else the option's (its year lines' labour codes, #515 slice 1b). */
  servicingLabourType: string | null;
  setup: MaintenanceSetup;
  contract: PricedContract;
  yearly: YearlySummary;
}

const isMaintenanceOption = (option: DeviceRecord): boolean => option.fields.hig_kind === OPTION_KIND.maintenance;

/** The contract for a maintenance option; null for a repaint; an error (a missing setting, a bad term) said plainly. */
export function priceMaintenance(
  option: DeviceRecord,
  reference: PricingReferenceFromApi,
  columns: OptionColumns,
  sections: SectionInput[],
  priced: PricedOption,
  rates: PricingRates,
): MaintenancePricing | { error: string } | null {
  if (!isMaintenanceOption(option)) return null;
  const years = maintenanceYearsOf(option.fields);
  try {
    const setup = maintenanceSetup(reference, columns, years);
    const contract = priceContract(sections, priced, rates, setup, programmesOf(columns.scope, priced.sections.map((s) => s.id), years));
    const startYear = columns.scope.maintenance?.startYear ?? defaultStartYear(reference);
    const tier = typeof option.fields.hig_maintenancetier === 'string' ? option.fields.hig_maintenancetier : null;
    const servicingLabourType = columns.scope.maintenance?.servicingLabourType || columns.labourType;
    return { years, startYear, tier, servicingLabourType, setup, contract, yearly: yearlySummary(contract, startYear, setup.payment) };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

/** The REVIEW questions for a version: the maintenance group only when one of its options is a maintenance contract (#515). */
export function reviewQuestionsFor(questions: readonly ReviewQuestionFromApi[], options: readonly DeviceRecord[]): readonly ReviewQuestionFromApi[] {
  return options.some(isMaintenanceOption) ? questions : questions.filter((q) => q.group !== MAINTENANCE_GROUP);
}

/** The set-up's figures beside the REVIEW's maintenance questions, as coats and hire are shown beside theirs. */
export function maintenanceFigures(pricing: MaintenancePricing): Record<string, string> {
  return {
    maintenanceYears: `${pricing.years} years`,
    maintenanceStartYear: String(pricing.startYear),
    servicingAbove3m: `Set-up: ${pricing.setup.includeAccess ? 'Yes' : 'No'}`,
  };
}

/** The maintenance proposal is its tier's template (#515), which has ten years. */
const MAINTENANCE_PROPOSAL_YEARS = 10;
const NO_TIER = 'Pick the tier (Enhance, Maintain or Protect) first: it decides which proposal is made.';

/** Why a maintenance contract's proposal can't be made, besides the REVIEW; null = it can. */
export function maintenanceProposalBlocker(contract: MaintenancePricing): string | null {
  if (!contract.tier) return NO_TIER;
  if (contract.years > MAINTENANCE_PROPOSAL_YEARS) return `The maintenance proposal has ${MAINTENANCE_PROPOSAL_YEARS} years: make a ${contract.years}-year contract's in Templafy as today.`;
  return null;
}

export const maintenanceProposalAbout = (contract: MaintenancePricing) =>
  `The Maintenance ${contract.tier ?? ''} Proposal for this contract, in Word: cover letter, each area's Schedule of Work year by year, and the Payment Summary (the one-off repaint, PAYG by year, and Tailored for Even Spread or Higher Year One) filled in. It opens from the job’s SharePoint folder; finish the tier's Yes/No and hours, and the rest, in Word.`;
